import type { FC, ReactNode } from "react";
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from "recharts";

type PieData = { name: string; value: number; color?: string };
type Counts = {
  InProductionCarrierCount?: number;
  NotInProductionCarrierCount?: number;
  inProductionCarrierCount?: number;
  notInProductionCarrierCount?: number;
  [k: string]: any;
};

const PIE_COLORS = ["#2563EB", "#F59E0B"];

const CarrierStatusPieChart: FC<{
  data?: PieData[];
  counts?: Counts | null;
  height?: number;
}> = ({ data, counts = null, height = 180 }) => {
  const chartData: PieData[] = (() => {
    if (Array.isArray(data) && data.length) {
      return data.map((d, i) => ({ ...d, value: Number(d.value || 0), color: d.color ?? PIE_COLORS[i % PIE_COLORS.length] }));
    }
    if (counts) {
      const inProd = Number(counts.InProductionCarrierCount ?? counts.inProductionCarrierCount ?? 0);
      const notInProd = Number(counts.NotInProductionCarrierCount ?? counts.notInProductionCarrierCount ?? 0);
      if (inProd === 0 && notInProd === 0) return [];
      return [
        { name: "In Production", value: inProd, color: PIE_COLORS[0] },
        { name: "Not In Production", value: notInProd, color: PIE_COLORS[1] },
      ];
    }
    return [];
  })();

  if (!chartData.length) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-gray-400" style={{ height }}>
        No data
      </div>
    );
  }

  const renderPieLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, percent, value, index } = props;
    if (percent < 0.03) return null;
    const RAD = Math.PI / 180;
    const r = outerRadius + 14;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text x={x} y={y} dy={4} textAnchor={x > cx ? "start" : "end"} fontWeight={700} fontSize={11} fill="#111827">
        {value} ({Math.round(percent * 100)}%)
      </text>
    );
  };

  const renderLabelLine = (props: any) => {
    const { cx, cy, midAngle, outerRadius, index, percent } = props;
    if (percent < 0.03) return <g />;
    const RAD = Math.PI / 180;
    const sx = cx + outerRadius * Math.cos(-midAngle * RAD);
    const sy = cy + outerRadius * Math.sin(-midAngle * RAD);
    const mx = cx + (outerRadius + 8) * Math.cos(-midAngle * RAD);
    const my = cy + (outerRadius + 8) * Math.sin(-midAngle * RAD);
    const ex = cx + (outerRadius + 14) * Math.cos(-midAngle * RAD);
    const ey = cy + (outerRadius + 14) * Math.sin(-midAngle * RAD);
    const sliceColor = chartData[index]?.color ?? PIE_COLORS[index % PIE_COLORS.length];
    return <polyline points={`${sx},${sy} ${mx},${my} ${ex},${ey}`} stroke={sliceColor} strokeWidth={1} fill="none" />;
  };

  const renderTooltip = (props: { active?: boolean; payload?: any[]; label?: string | number }) => {
    const { active, payload, label } = props || {};
    if (!active || !payload || !payload.length) return null;

    const item = payload[0];
    const idx = item?.payload?.index ?? item?.index;
    const matchedByIndex = typeof idx === "number" ? chartData[idx] : undefined;

    const payloadName = item?.payload?.name ?? item?.name ?? label;
    const matchedByName = !matchedByIndex ? chartData.find((d) => d.name === payloadName) : undefined;

    const matched = matchedByIndex ?? matchedByName;

    const valueRaw = matched?.value ?? item?.payload?.value ?? item?.value ?? 0;
    const nameRaw = matched?.name ?? item?.payload?.displayName ?? item?.payload?.name ?? item?.name ?? label ?? "Value";
    const color = matched?.color ?? item?.payload?.color ?? item?.color;

    const value: ReactNode = valueRaw;
    const name: ReactNode = nameRaw;

    return (
      <div className="bg-white border border-gray-200 rounded-md shadow-sm p-2" style={{ pointerEvents: "none", minWidth: 88 }}>
        <div className="text-lg font-semibold text-center">{value}</div>
        <div className="text-sm mt-1 text-center" style={{ color }}>
          {color ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ background: color, display: "inline-block" }} />
              <span>{name}</span>
            </span>
          ) : (
            <span>{name}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius="30%"
            outerRadius="85%"
            paddingAngle={2}
            cornerRadius={4}
            isAnimationActive
            stroke="#fff"
            strokeWidth={1}
            labelLine={renderLabelLine}
            label={renderPieLabel}
          >
            {chartData.map((entry, i) => (
              <Cell key={`${entry.name}-${i}`} fill={entry.color ?? PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>

          <Tooltip content={renderTooltip} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CarrierStatusPieChart;
